import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Clock, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";

const TABS = ["Overview", "Timeline", "People", "Files", "Tasks", "Approvals", "AI", "Financial", "Design", "Operations", "Procurement", "Tickets"];
const BUILT = ["Overview", "Timeline", "People", "Tasks"];

function EmptyTab({ name }) {
  return <div className="py-16 text-center text-sm text-neutral-400" data-testid={`project-tab-empty-${name}`}>The <b>{name}</b> section activates in a later ILLAM build.</div>;
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [p, setP] = useState(null);
  const [timeline, setTimeline] = useState({ events: [], comments: [] });
  const [tasks, setTasks] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [comment, setComment] = useState("");

  const load = () => api.get(`/projects/${id}`).then((r) => setP(r.data)).catch(() => navigate("/projects"));
  const loadTimeline = () => api.get(`/projects/${id}/timeline`).then((r) => setTimeline(r.data)).catch(() => {});
  useEffect(() => {
    load(); loadTimeline();
    api.get(`/tasks`, { params: { project_id: id } }).then((r) => setTasks(r.data)).catch(() => {});
    api.get("/projects/statuses").then((r) => setStatuses(r.data.statuses)).catch(() => {});
  }, [id]); // eslint-disable-line

  const transition = async (status) => {
    try { await api.post(`/projects/${id}/transition`, { status }); toast.success(`Moved to ${status}`); load(); loadTimeline(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    try { await api.post("/comments", { entity_type: "project", entity_id: id, comment, visibility: "internal" }); setComment(""); loadTimeline(); toast.success("Comment added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!p) return <AppShell><div className="text-sm text-neutral-400">Loading…</div></AppShell>;

  return (
    <AppShell>
      <button onClick={() => navigate("/projects")} className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-800 mb-3" data-testid="project-back-button"><ArrowLeft size={15} /> Projects</button>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl font-bold tracking-tight text-neutral-900">{p.project_name}</h1>
            <StatusBadge status={p.status} testid="project-status-badge" />
          </div>
          <div className="mono text-sm text-neutral-500 mt-1">{p.project_code}</div>
        </div>
        {can("projects.edit") && (
          <div className="flex items-center gap-2">
            <select data-testid="project-transition-select" defaultValue="" onChange={(e) => e.target.value && transition(e.target.value)} className="text-sm rounded-md border border-[#D7C6A9] bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-[#F56B1F]">
              <option value="">Move stage…</option>
              {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
      </div>

      <Tabs defaultValue="Overview">
        <TabsList className="bg-[#EEE9DE] flex-wrap h-auto">
          {TABS.map((t) => <TabsTrigger key={t} value={t} data-testid={`project-detail-tab-${t.toLowerCase()}`} className="data-[state=active]:bg-white data-[state=active]:text-[#F56B1F] text-xs">{t}</TabsTrigger>)}
        </TabsList>

        <TabsContent value="Overview">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-4">
            <div className="lg:col-span-2 bg-white rounded-lg border border-[#D7C6A9] p-5">
              <h3 className="font-heading font-semibold text-neutral-900 mb-4">Project Details</h3>
              <dl className="grid grid-cols-2 gap-y-4 gap-x-6 text-sm">
                {[
                  ["Customer", p.customer ? `${p.customer.first_name} ${p.customer.last_name}` : "—"],
                  ["Type", p.project_type],
                  ["Designer", p.designer?.name || "Unassigned"],
                  ["Project Manager", p.project_manager?.name || "Unassigned"],
                  ["Current Stage", p.current_stage],
                  ["Property", p.property ? `${p.property.city}, ${p.property.state}` : "—"],
                ].map(([k, v]) => (
                  <div key={k}><dt className="text-xs text-neutral-500 uppercase tracking-wider">{k}</dt><dd className="text-neutral-900 mt-0.5">{v}</dd></div>
                ))}
              </dl>
            </div>
            <div className="bg-white rounded-lg border border-[#D7C6A9] p-5">
              <h3 className="font-heading font-semibold text-neutral-900 mb-4">Stages</h3>
              <div className="space-y-3">
                {(p.stages || []).map((s) => (
                  <div key={s.id} className="flex items-center gap-3" data-testid={`project-stage-${s.stage_type}`}>
                    <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${s.status === "Completed" ? "bg-[#166534]" : "bg-[#F56B1F]"}`} />
                    <span className="text-sm text-neutral-800 flex-1">{s.stage_type}</span>
                    <span className="text-[11px] text-neutral-400">{s.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="Timeline">
          <div className="mt-4 bg-white rounded-lg border border-[#D7C6A9] p-5">
            <div className="flex gap-2 mb-4">
              <input data-testid="project-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} placeholder="Add a comment…" className="flex-1 rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F]" />
              <button onClick={addComment} data-testid="project-comment-submit" className="rounded-md bg-[#F56B1F] hover:bg-[#D65A10] text-white px-3 transition-colors"><Send size={16} /></button>
            </div>
            <div className="relative pl-6 border-l-2 border-[#D7C6A9] space-y-5">
              {timeline.comments.map((c) => (
                <div key={c.id} className="relative" data-testid="timeline-comment">
                  <span className="absolute -left-[31px] top-0.5 h-4 w-4 rounded-full bg-white border-2 border-[#F56B1F] flex items-center justify-center"><MessageSquare size={9} className="text-[#F56B1F]" /></span>
                  <p className="text-sm text-neutral-800">{c.comment}</p>
                  <span className="mono text-[11px] text-neutral-400">{new Date(c.created_at).toLocaleString()}</span>
                </div>
              ))}
              {timeline.events.map((e) => (
                <div key={e.id} className="relative" data-testid={`timeline-event-${e.event_type}`}>
                  <span className="absolute -left-[31px] top-0.5 h-4 w-4 rounded-full bg-white border-2 border-[#D7C6A9] flex items-center justify-center"><Clock size={9} className="text-neutral-400" /></span>
                  <p className="text-sm text-neutral-800"><b className="font-medium">{e.actor_name}</b> · {e.event_type.replaceAll("_", " ").toLowerCase()}</p>
                  <span className="mono text-[11px] text-neutral-400">{new Date(e.created_at).toLocaleString()}</span>
                </div>
              ))}
              {timeline.events.length === 0 && timeline.comments.length === 0 && <p className="text-sm text-neutral-400">No activity yet.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="People">
          <div className="mt-4 bg-white rounded-lg border border-[#D7C6A9] p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[["Designer", p.designer?.name], ["Project Manager", p.project_manager?.name]].map(([role, name]) => (
              <div key={role} className="flex items-center gap-3 p-3 rounded-md bg-[#F5F2EB]">
                <div className="h-10 w-10 rounded-full bg-[#3E3E3E] text-white flex items-center justify-center text-xs font-semibold">{(name || "NA").split(" ").map((x) => x[0]).slice(0, 2).join("")}</div>
                <div><div className="text-sm font-medium text-neutral-900">{name || "Unassigned"}</div><div className="text-xs text-neutral-500">{role}</div></div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="Tasks">
          <div className="mt-4 bg-white rounded-lg border border-[#D7C6A9] overflow-hidden">
            {tasks.length === 0 ? <div className="py-12 text-center text-sm text-neutral-400">No tasks for this project.</div> : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 bg-[#FafaF8]"><th className="px-5 py-2.5">Task</th><th className="px-5 py-2.5">Priority</th><th className="px-5 py-2.5">Status</th></tr></thead>
                <tbody>{tasks.map((t) => <tr key={t.id} className="border-t border-[#EEE9DE]"><td className="px-5 py-3">{t.title}</td><td className="px-5 py-3">{t.priority}</td><td className="px-5 py-3"><StatusBadge status={t.status} /></td></tr>)}</tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {TABS.filter((t) => !BUILT.includes(t)).map((t) => <TabsContent key={t} value={t}><EmptyTab name={t} /></TabsContent>)}
      </Tabs>
    </AppShell>
  );
}
